import defaultShareImage from "@/assets/images/logoFederacion.png";
import { obtenerSiteSettingsMap } from "@/services/siteSettings/siteSettingsService";

const ROUTE_SEO_KEYS = {
  home: "home",
  noticias: "noticias",
  calendario: "calendario",
  resultados: "resultados",
  federacion: "federacion",
};

const DEFAULT_TITLE = "Federación de Bochas Córdoba";
const DEFAULT_DESCRIPTION =
  "Sitio oficial de la Federación de Bochas de la Provincia de Córdoba.";
const DEFAULT_SITE_URL = "https://bochascba.com.ar";
const INDEX_ROBOTS =
  "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

function setMeta(attribute, key, content) {
  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!content) {
    tag?.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(url) {
  let tag = document.head.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", "canonical");
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", url);
}

function cleanBaseUrl(value) {
  const fallback =
    process.env.NODE_ENV === "production"
      ? DEFAULT_SITE_URL
      : window.location.origin;
  try {
    return new URL(value || fallback).origin;
  } catch (_) {
    return fallback;
  }
}

function absoluteUrl(value, baseUrl) {
  if (!value) return "";
  try {
    return new URL(value, `${baseUrl}/`).href;
  } catch (_) {
    return "";
  }
}

function publicUrl(baseUrl, path = window.location.pathname) {
  return new URL(path || "/", `${baseUrl}/`).href;
}

function plainText(value) {
  if (!value) return "";
  const template = document.createElement("template");
  template.innerHTML = String(value);
  return (template.content.textContent || "").replace(/\s+/g, " ").trim();
}

function applyMetadata({
  title,
  description,
  image,
  url,
  type = "website",
  robots = INDEX_ROBOTS,
}) {
  const finalTitle = title || DEFAULT_TITLE;
  const finalDescription = description || DEFAULT_DESCRIPTION;
  document.title = finalTitle;
  setMeta("name", "description", finalDescription);
  setMeta("name", "robots", robots);
  setMeta("property", "og:locale", "es_AR");
  setMeta("property", "og:type", type);
  setMeta("property", "og:site_name", DEFAULT_TITLE);
  setMeta("property", "og:title", finalTitle);
  setMeta("property", "og:description", finalDescription);
  setMeta("property", "og:url", url);
  setMeta("property", "og:image", image);
  setMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
  setMeta("name", "twitter:title", finalTitle);
  setMeta("name", "twitter:description", finalDescription);
  setMeta("name", "twitter:image", image);
  setCanonical(url);
}

export async function applyRouteSeo(route) {
  try {
    const settings = await obtenerSiteSettingsMap();
    const baseUrl = cleanBaseUrl(settings["seo.siteUrl"]);
    const routeKey = ROUTE_SEO_KEYS[route.name] || "";
    const title =
      settings[`seo.${routeKey}Title`] ||
      settings["seo.defaultTitle"] ||
      DEFAULT_TITLE;
    const description =
      settings[`seo.${routeKey}Description`] ||
      settings["seo.defaultDescription"] ||
      DEFAULT_DESCRIPTION;
    const image = absoluteUrl(
      settings["seo.defaultImage"] || defaultShareImage,
      baseUrl,
    );
    const privatePage =
      route.path.startsWith("/admin") ||
      route.name === "login" ||
      route.name === "not-found";

    applyMetadata({
      title,
      description,
      image,
      url: publicUrl(baseUrl, route.path),
      robots: privatePage ? "noindex, nofollow" : INDEX_ROBOTS,
    });
  } catch (_) {
    // Mantener los metadatos iniciales si la configuración no está disponible.
  }
}

export async function applyContentSeo({
  title,
  description,
  content,
  image,
  type = "article",
}) {
  try {
    const settings = await obtenerSiteSettingsMap();
    const baseUrl = cleanBaseUrl(settings["seo.siteUrl"]);
    const finalDescription =
      plainText(description || content).slice(0, 180) ||
      settings["seo.defaultDescription"] ||
      DEFAULT_DESCRIPTION;
    const finalImage = absoluteUrl(
      image || settings["seo.defaultImage"] || defaultShareImage,
      baseUrl,
    );

    applyMetadata({
      title: plainText(title) || settings["seo.defaultTitle"] || DEFAULT_TITLE,
      description: finalDescription,
      image: finalImage,
      url: publicUrl(baseUrl),
      type,
    });
  } catch (_) {
    // La navegación conserva el SEO general como respaldo.
  }
}

export function applyNotFoundSeo() {
  const baseUrl = cleanBaseUrl();
  applyMetadata({
    title: `Contenido no encontrado | ${DEFAULT_TITLE}`,
    description: DEFAULT_DESCRIPTION,
    image: absoluteUrl(defaultShareImage, baseUrl),
    url: publicUrl(baseUrl),
    robots: "noindex, nofollow",
  });
}
